import { Formik } from "formik";
import Loader from "../../loader";
import TagInput from "../../form/TagInput";
import StepsDots from "../../form/StepsDots";
import ErrorMessage from "../../form/ErrorMessage";
import { useDispatch, useSelector } from "react-redux";
import { profileStep2Schema } from "../../../utils/schema";
import { setRegisterStep } from "../../../redux/features/find-a-job/reducers/authReducer";
import { getProfile, setProfile, updateFreelancerFormData } from "../../../utils/pip";
import {
  fetchfreelancerProfile,
  profileStep2Submit,
} from "../../../redux/features/find-a-job/actions/authAction";
import { useEffect, useState } from "react";
import { message } from "antd";

const ProfileSetupStep2 = ({ stepBack }) => {
  const dispatch = useDispatch();
  const { subLoading } = useSelector((state) => state.freelancer.auth);
  const [savedData, setSavedData] = useState({});

  const splitList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value) return [];
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const toggleAvailability = (
    currentList,
    value,
    setFieldValue,
    setFieldError,
    setFieldTouched
  ) => {
    const current = new Set(currentList || []);
    if (current.has(value)) {
      current.delete(value);
    } else {
      if (current.size >= 3) {
        setFieldError("availability", undefined);
        message.error("You can select up to 3 availability options.");
        return;
      }
      current.add(value);
    }
    setFieldTouched("availability", true, false);
    setFieldValue("availability", Array.from(current));
  };

  const refreshStep1FromProfile = async () => {
    const res = await dispatch(fetchfreelancerProfile());
    const data = res?.payload?.data;
    if (!data) return;
    const existing = getProfile("freelancer") || {};
    const merged = {
      ...existing,
      ...data,
      full_name:
        data.full_name ||
        existing.full_name ||
        `${data.first_name || ""} ${data.last_name || ""}`.trim(),
      first_name: data.first_name || existing.first_name || data.full_name || "",
      last_name: data.last_name || existing.last_name || "",
    };
    setProfile("freelancer", merged);
    const openTalent = merged.open_talent || {};
    updateFreelancerFormData("profileStep1", {
      phone_number: openTalent.phone_number || "",
      country: openTalent.country || "",
      city: openTalent.city || "",
      linkedin_profile: openTalent.linkedin_profile || "",
      portfolio_website:
        openTalent.website_or_portfolio || openTalent.portfolio_website || "",
      bio: openTalent.bio || "",
    });
  };

  const handleStep2 = (values) => {
    const resolvedPrimaryRole =
      values?.primary_role === "Other"
        ? values?.other_primary_role || ""
        : values?.primary_role || "";
    updateFreelancerFormData("profileStep2", {
      ...values,
      primary_role: resolvedPrimaryRole,
    });

    const payload = {
      primary_role: resolvedPrimaryRole,
      years_of_experience: values?.years_of_experience || "",
      skills: (values?.key_skills || []).join(", "),
      tools_software_used: (values?.tools || []).join(", "),
      availability: (values?.availability || []).join(", "),
      hourly_rate: values?.hourly_rate || "",
      preferred_project_type: (values?.preferred_project_type || []).join(", "),
    };

    const callback = (response) => {
      if (!response?.success) return;
      dispatch(setRegisterStep("profileStep3"));
    };

    dispatch(profileStep2Submit({ payload, callback }));
  };

  useEffect(() => {
    dispatch(fetchfreelancerProfile()).then((res) => {
      const data = res?.payload?.data || {};
      const openTalent = data?.open_talent || {};
      const primaryRole = openTalent?.primary_role || "";
      const isOtherPrimaryRole =
        primaryRole && !roleOptions.includes(primaryRole);
      setSavedData({
        primary_role: isOtherPrimaryRole ? "Other" : primaryRole,
        other_primary_role: isOtherPrimaryRole ? primaryRole : "",
        years_of_experience: openTalent?.years_of_experience || "",
        key_skills: splitList(openTalent?.skills),
        tools: splitList(openTalent?.tools_software_used),
        availability: splitList(openTalent?.availability),
        hourly_rate: openTalent?.hourly_rate || "",
        preferred_project_type: splitList(openTalent?.preferred_project_type),
      });
    });
  }, [dispatch]);

  const handlePrevious = async () => {
    await refreshStep1FromProfile();
    dispatch(setRegisterStep("profileStep1"));
  };

  const roleOptions = [
    "UX Designer",
    "UI Designer",
    "UX Researcher",
    "Graphic Designer",
    "Product Designer",
    "Interaction Designer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Web Developer",
    "Mobile App Developer (iOS)",
    "Mobile App Developer (Android)",
    "Cross-Platform App Developer",
    "Software Engineer",
    "Application Developer",
    "API Developer",
    "DevOps Engineer",
    "Cloud Engineer",
    "Site Reliability Engineer (SRE)",
    "Infrastructure Engineer",
    "Data Analyst",
    "Data Scientist",
    "Machine Learning Engineer",
    "AI Engineer",
    "Data Engineer",
    "Cybersecurity Specialist",
    "Security Engineer",
    "Penetration Tester",
    "QA Engineer",
    "Test Automation Engineer",
    "Manual Tester",
    "Database Administrator",
    "Systems Administrator",
    "Network Engineer",
    "IT Support Specialist",
    "Blockchain Developer",
    "Game Developer",
    "Embedded Systems Engineer",
    "Technical Project Manager",
    "IT Project Manager",
    "Product Manager",
    "Scrum Master",
    "Agile Coach",
    "Business Analyst",
    "Technical Consultant",
    "Solutions Architect",
    "Enterprise Architect",
    "Other",
  ];
  const experienceOptions = [
    "0-2 years",
    "3-5 years",
    "6-10 years",
    "10+ years",
  ];
  const availabilityOptions = [
    { label: "Remote Freelance", value: "Remote Freelance" },
    { label: "Full-time Freelance", value: "Full-time Freelance" },
    { label: "Part-time Freelance", value: "Part-time Freelance" },
    { label: "Hybrid Freelance", value: "Hybrid Freelance" },
    { label: "On-Site Freelance", value: "On-Site Freelance" },
    { label: "Project-Based", value: "Project Based" },
  ];
  const projectTypeOptions = [
    "Short-term projects",
    "Long-term contracts",
    "Startup collaborations",
    "Corporate clients",
  ];

  if (subLoading) {
    return <Loader />;
  }
  return (
    <div className="modal-body pe-0">
      <section className="ct_signup_main py-3">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-xxl-4 col-xl-4 col-lg-4 mb-4 mb-lg-0 d-none d-lg-block">
              <div className="ct_signup_left_img">
                <img
                  src="../assets/img/faq_img.png"
                  alt=""
                  style={{ objectFit: "cover" }}
                />
              </div>
            </div>
            <div className="col-xxl-8 col-xl-8 col-lg-8 mb-4 mb-lg-0 ">
              <div className="ct_signup_right_content px-5 ct_custom_scroll">
                <div className="ct_signup_logo ct_mb_34">
                  <img src="../assets/img/Nuvio_logo.png" alt="" />
                </div>
                <div className="mt-0 text-center">
                  <h2 className="ct_fs_32  mb-0 ct_darkblue_text">
                    Professional Information
                  </h2>
                  <p className="mb-0 ct_fs_16 mt-2 ct_darkblue_text mx-auto">
                    Share your role, skills, and work preferences so we can match
                    you with the right opportunities.
                  </p>
                </div>
                <Formik
                  initialValues={{
                    primary_role: savedData?.primary_role || "",
                    other_primary_role: savedData?.other_primary_role || "",
                    years_of_experience: savedData?.years_of_experience || "",
                    key_skills: savedData?.key_skills || [],
                    tools: savedData?.tools || [],
                    availability: savedData?.availability || [],
                    hourly_rate: savedData?.hourly_rate || "",
                    preferred_project_type: savedData?.preferred_project_type || [],
                  }}
                  validationSchema={profileStep2Schema}
                  enableReinitialize
                  onSubmit={(values, actions) => {
                    handleStep2(values);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched,
                  }) => (
                    <form onSubmit={handleSubmit} className="ct_mt_40">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Primary Role / Profession
                            </label>
                            <div className="d-flex flex-column gap-2 position-relative">
                              <select
                                className="form-control ct_input"
                                id="primary_role"
                                onBlur={handleBlur}
                                onChange={(event) => {
                                  const nextValue = event.target.value;
                                  setFieldValue("primary_role", nextValue);
                                  if (nextValue !== "Other") {
                                    setFieldValue("other_primary_role", "");
                                  }
                                }}
                                value={values?.primary_role}
                              >
                                <option value="">Select role</option>
                                {roleOptions.map((role) => (
                                  <option key={role} value={role}>
                                    {role}
                                  </option>
                                ))}
                              </select>
                              <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"primary_role"}
                            />
                          </div>
                          {values?.primary_role === "Other" && (
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Enter Primary Role
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                id="other_primary_role"
                                name="other_primary_role"
                                placeholder="Enter role"
                                value={values?.other_primary_role}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                          )}
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Years of Experience
                            </label>
                            <div className="d-flex flex-column gap-2 position-relative">
                              <select
                                className="form-control ct_input"
                                id="years_of_experience"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values?.years_of_experience}
                              >
                                <option value="">Select years</option>
                                {experienceOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"years_of_experience"}
                            />
                          </div>
                        </div>
                        <TagInput
                          label="Key Skills"
                          placeholder="Type to add skills..."
                          name="key_skills"
                          value={values.key_skills}
                          onChange={setFieldValue}
                          error={touched.key_skills && errors.key_skills}
                        />
                        <TagInput
                          label="Tools & Software"
                          placeholder="Type to add tools..."
                          name="tools"
                          value={values.tools}
                          onChange={setFieldValue}
                          error={touched.tools && errors.tools}
                        />
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Availability
                            </label>
                            <div className="d-flex flex-column gap-2">
                              {availabilityOptions.map((option) => (
                                <label key={option.value} className="ct_checkbox_label d-flex gap-2">
                                  <input
                                    type="checkbox"
                                    checked={(values.availability || []).includes(option.value)}
                                    onChange={() =>
                                      toggleAvailability(
                                        values.availability,
                                        option.value,
                                        setFieldValue,
                                        setFieldError,
                                        setFieldTouched
                                      )
                                    }
                                  />
                                  <span className="ct_fs_14 ct_darkblue_text">
                                    {option.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"availability"}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Hourly Rate (Optional)
                            </label>
                            <input
                              className="form-control ct_input"
                              placeholder="Hourly Rate"
                              type="text"
                              id="hourly_rate"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values?.hourly_rate}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"hourly_rate"}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Preferred Project Type
                            </label>
                            <div className="d-flex flex-column gap-2">
                              {projectTypeOptions.map((type) => (
                                <label key={type} className="ct_checkbox_label d-flex gap-2">
                                  <input
                                    type="checkbox"
                                    checked={values.preferred_project_type?.includes(type)}
                                    onChange={() => {
                                      const current = new Set(values.preferred_project_type || []);
                                      if (current.has(type)) {
                                        current.delete(type);
                                      } else {
                                        current.add(type);
                                      }
                                      setFieldValue("preferred_project_type", Array.from(current));
                                    }}
                                  />
                                  <span className="ct_fs_14 ct_darkblue_text">{type}</span>
                                </label>
                              ))}
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"preferred_project_type"}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="ct_outline_btn w-100 mx-auto"
                          onClick={handlePrevious}
                        >
                          Previous
                        </button>
                        <button
                          type="submit"
                          onClick={handleSubmit}
                          className="ct_yellow_btn w-100 mx-auto"
                        >
                          Continue
                        </button>
                      </div>
                      <StepsDots steps={[1, 2, 3, 4]} activeStep={2} />
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfileSetupStep2;
